import React, { useEffect, useState } from 'react';
import PageContainer from '../../../components/PageContainer';
import { Box, Button, capitalize, Chip, CircularProgress, Dialog, DialogActions, DialogContent, Grid, IconButton } from '@mui/material';
import * as Yup from 'yup';
import { Add, DeleteOutline, Edit } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import appRoutes from '../../../routes/routeConfig';
import moment from 'moment';
import Filters from '../../../components/Filters';
import { DataGrid } from '@mui/x-data-grid';
import { getPrivilegeStatus, getStatusColor } from '../../../util';
import { useSnackbar } from 'notistack';
import { addFaq, addRole, deleteFaq, getFaqs, getRoles, removeRole } from '../../../redux/api/userMgt';
import { Form, Formik } from 'formik';
import TextInput from '../../../components/common/TextInput';
import SelectInput from '../../../components/common/SelectInput';

const Faqs = () => {
    const defFaq = {
        // "faqId": 0,
        question: '',
        answer: '',
        selectedForPreview: false,
    };
    const [processing, setProcessing] = useState(false);
    const [actionModal, setActionModal] = useState(null);
    const [selectedFaq, setSelectedFaq] = useState(defFaq);
    const { enqueueSnackbar } = useSnackbar();
    const [records, setRecords] = useState([]);

    const columns = [
        {
            field: 'sn',
            headerName: 'S/N',
            width: 80,
            sortable: false,
            renderCell: (params) => {
                return <p style={{ margin: 'auto' }}>{(params?.api?.getRowIndex(params?.row?.faqId) || 0) + 1}</p>;
            },
        },
        {
            field: 'action',
            headerName: 'Quick Action',
            width: 150,
            sortable: false,
            renderCell: (params) => {
                return (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        {getPrivilegeStatus('AddOrUpdateFAQ') && (
                            <IconButton
                                size="small"
                                color={'primary'}
                                onClick={() => {
                                    setSelectedFaq(params?.row || null);
                                    setActionModal('edit');
                                }}
                                variant="outlined"
                                title={'Edit FAQ'}
                            >
                                <Edit />
                            </IconButton>
                        )}
                        {getPrivilegeStatus('DeleteFAQ') && (
                            <IconButton
                                size="small"
                                color={'error'}
                                onClick={() => {
                                    setSelectedFaq(params?.row || null);
                                    setActionModal('delete');
                                }}
                                variant="outlined"
                                title={'Delete FAQ'}
                            >
                                <DeleteOutline />
                            </IconButton>
                        )}
                    </div>
                );
            },
        },
        { field: 'question', headerName: 'Question', width: 350 },
        { field: 'answer', headerName: 'Answer', width: 400 },
        {
            field: 'selectedForPreview',
            headerName: 'Selected For Preview',
            width: 350,
        },
        // { field: 'createdBy', headerName: 'Created By', width: 200 },
        // { field: 'createdDate', headerName: 'Creation Date', width: 200, valueGetter: (params) => (params?.row?.createdDate) ? (moment(params?.row?.createdDate).format('DD MMMM, YYYY')) : '' },
        // { field: 'createdTime', headerName: 'Creation Time', width: 200, valueGetter: (params) => (params?.row?.createdDate) ? (moment(params?.row?.createdDate).format('hh:mm:ss A')) : '' }
    ];

    const init = async () => {
        try {
            setProcessing(true);
            const res = await getFaqs();
            setRecords(res?.resultObject || []);
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setProcessing(false);
        }
    };

    const remove = async (values) => {
        try {
            await deleteFaq(values.faqId || '');
            setActionModal(false);
            setSelectedFaq(defFaq);
            enqueueSnackbar('FAQ Deleted Successfully');
            init();
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        }
    };

    const create = async (values) => {
        try {
            await addFaq({
                ...values,
                selectedForPreview: Boolean(values.selectedForPreview == 'true'),
            });
            setSelectedFaq(defFaq);
            enqueueSnackbar(`FAQ ${capitalize(actionModal || '')} Successfully`);
            setActionModal(false);
            init();
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        }
    };

    useEffect(() => {
        init();
    }, []);

    return (
        <PageContainer
            pageTitle="FAQs"
            tagline="Manage all FAQs on the public portal"
            processing={processing}
            buttonComponent={
                getPrivilegeStatus('AddOrUpdateFAQ') && (
                    <>
                        <Button
                            onClick={() => {
                                setActionModal('create');
                                setSelectedFaq(defFaq);
                            }}
                            startIcon={<Add />}
                            variant="contained"
                        >
                            Create FAQ
                        </Button>
                    </>
                )
            }
        >
            <Box>
                <div className="paper" style={{ height: 650, width: '100%', textAlign: 'center' }}>
                    <DataGrid
                        className={'center-first-column'}
                        rows={records}
                        getRowId={(row) => row.faqId}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10]}
                        checkboxSelection={false}
                        disableSelectionOnClick
                        style={{ fontSize: '12px' }}
                    />
                </div>
            </Box>

            <Dialog open={Boolean(actionModal)} maxWidth="lg" PaperProps={{ style: { minWidth: '50vw' } }}>
                <Formik
                    initialValues={{ ...selectedFaq }}
                    enableReinitialize
                    onSubmit={actionModal === 'delete' ? remove : create}
                    validationSchema={Yup.object().shape(
                        actionModal === 'create'
                            ? {
                                  question: Yup.string().required('Question is required.'),
                                  answer: Yup.string().required('Answer is required.'),
                                  selectedForPreview: Yup.string().required('Selected For Preview is required.'),
                              }
                            : {}
                    )}
                >
                    {({ isSubmitting, values }) => (
                        <Form>
                            <DialogContent>
                                {actionModal === 'delete' ? (
                                    <>
                                        <h4 className="mb-1">Delete FAQ</h4>
                                        <p className="mb-0">Kindly confirm deletion of FAQ with ID: {selectedFaq?.faqId}</p>
                                    </>
                                ) : (
                                    <>
                                        <h4 className="mb-1">Create FAQ</h4>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <TextInput id="question" required label="Question" />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextInput id="answer" multiline required minRows={2} label="Answer" />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <SelectInput
                                                    options={[{ code: 'true' }, { code: 'false' }]}
                                                    value={String(values.selectedForPreview)}
                                                    field="code"
                                                    fieldDisplay="code"
                                                    id="selectedForPreview"
                                                    label="Selected for Preview"
                                                />
                                            </Grid>
                                        </Grid>
                                    </>
                                )}
                            </DialogContent>
                            <DialogActions className="pb-1 pr-1">
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => {
                                        setActionModal(null);
                                        setSelectedFaq(defFaq);
                                    }}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </Button>
                                <Button variant="contained" color={actionModal === 'delete' ? 'error' : 'primary'} type="submit" disabled={isSubmitting}>
                                    {Boolean(isSubmitting) ? <CircularProgress color="inherit" size={15} /> : capitalize(actionModal || '')}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </PageContainer>
    );
};

export default Faqs;
